import { useEffect, useRef, useState } from 'react';
import { useLoadProgress } from '../lib/useLoadProgress';
import { fetchSiteSettings, LOADING_SCREEN_ENABLED_KEY } from '../lib/siteSettingsApi';

const SESSION_KEY = 'hs_loading_shown';

const MESSAGES = ['Lighting the diyas…', 'Brewing the masala chai…', 'Counting Amdavad stars…', 'Unfolding fresh linen…'];

const STARS: { top: string; left: string; size: number; d: string; dl: string }[] = [
  { top: '9%', left: '16%', size: 3, d: '2.8s', dl: '.2s' },
  { top: '14%', left: '72%', size: 2, d: '3.6s', dl: '1.1s' },
  { top: '6%', left: '48%', size: 2, d: '2.2s', dl: '.7s' },
  { top: '21%', left: '30%', size: 2, d: '4.1s', dl: '1.8s' },
  { top: '11%', left: '88%', size: 3, d: '3.1s', dl: '.4s' },
  { top: '26%', left: '60%', size: 2, d: '2.6s', dl: '2.2s' },
  { top: '18%', left: '8%', size: 2, d: '3.9s', dl: '1.4s' },
  { top: '30%', left: '82%', size: 2, d: '2.9s', dl: '.9s' },
  { top: '34%', left: '40%', size: 2, d: '3.3s', dl: '.5s' },
  { top: '8%', left: '33%', size: 2, d: '2.4s', dl: '1.6s' },
];

export default function LoadingScreen() {
  const [skip] = useState(() => sessionStorage.getItem(SESSION_KEY) === '1');
  const { progress, done } = useLoadProgress();
  const [fadingOut, setFadingOut] = useState(false);
  const [unmounted, setUnmounted] = useState(skip);
  const [messageIndex, setMessageIndex] = useState(0);
  // Defaults to shown (matches the pre-toggle behavior) until the admin
  // setting is confirmed disabled — see LOADING_SCREEN_ENABLED_KEY.
  const [enabled, setEnabled] = useState(true);
  const fadeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (skip) return;
    fetchSiteSettings()
      .then((s) => {
        if (s[LOADING_SCREEN_ENABLED_KEY] === '0') setEnabled(false);
      })
      .catch(() => {});
  }, [skip]);

  useEffect(() => {
    if (skip) return;
    const interval = setInterval(() => setMessageIndex((i) => i + 1), 2400);
    return () => clearInterval(interval);
  }, [skip]);

  useEffect(() => {
    if (skip || fadingOut || (!done && enabled)) return;
    sessionStorage.setItem(SESSION_KEY, '1');
    setFadingOut(true);
    fadeTimer.current = setTimeout(() => setUnmounted(true), 400);
    return () => {
      if (fadeTimer.current) clearTimeout(fadeTimer.current);
    };
  }, [done, enabled, fadingOut, skip]);

  if (unmounted) return null;

  return (
    <div
      className={`hs-loading-screen${fadingOut ? ' hs-loading-fadeout' : ''}`}
      role="status"
      aria-label="Loading Homeland Stay"
    >
      {STARS.map((s, i) => (
        <div
          key={i}
          className="hs-star"
          style={{ top: s.top, left: s.left, width: s.size, height: s.size, ['--d' as string]: s.d, ['--dl' as string]: s.dl }}
        />
      ))}

      <div className="hs-moon" />

      <div className="hs-loading-lockup">
        <img className="hs-loading-badge" src="/badge-cream.webp" alt="Homeland Stay badge" width={280} height={280} />
        <img className="hs-loading-wordmark" src="/wordmark-cream.webp" alt="Homeland Stay" width={330} height={106} />
      </div>

      <div className="hs-loading-footer">
        <div className="hs-loading-msg" key={messageIndex}>
          {MESSAGES[messageIndex % MESSAGES.length]}
        </div>
        <div className="hs-loading-track">
          <div className="hs-loading-fill" style={{ width: `${progress}%` }} />
        </div>
        <div className="hs-loading-pct">{Math.round(progress)}%</div>
      </div>
    </div>
  );
}
